import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { DEVICES_QUERY_KEY } from '@/lib/queries'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle2, XCircle, RefreshCw } from 'lucide-react'

type Step = 'creating' | 'waiting_qr' | 'scan' | 'authenticating' | 'ready' | 'error'

interface WhatsAppDeviceSetupContentProps {
  onDone?: () => void
}

export function WhatsAppDeviceSetupContent({ onDone }: WhatsAppDeviceSetupContentProps) {
  const [step, setStep] = useState<Step>('creating')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [deviceName, setDeviceName] = useState('WhatsApp Device')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const queryClient = useQueryClient()

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // Step 1: create session on mount
  useEffect(() => {
    let cancelled = false
    api.post('/whatsapp/sessions')
      .then(res => {
        if (!cancelled) {
          setSessionId(res.data.data.sessionId)
          setStep('waiting_qr')
        }
      })
      .catch(err => {
        if (!cancelled) {
          setErrorMsg(err?.response?.data?.message || 'Failed to start WhatsApp session')
          setStep('error')
        }
      })
    return () => { cancelled = true; stopPolling() }
  }, [])

  // Step 2+3: poll for QR then for READY status
  useEffect(() => {
    if (!sessionId || !['waiting_qr', 'scan', 'authenticating'].includes(step)) return

    stopPolling()
    intervalRef.current = setInterval(async () => {
      try {
        if (step === 'waiting_qr' || step === 'scan') {
          const { data } = await api.get(`/whatsapp/sessions/${sessionId}/qr`)
          const { qr: newQr, sessionStatus } = data.data

          if (sessionStatus === 'ready') {
            stopPolling()
            await ensureDeviceCreated()
            return
          }
          if (newQr && newQr !== qr) {
            setQr(newQr)
            setStep('scan')
          }
          if (sessionStatus === 'authenticating') {
            setStep('authenticating')
          }
        } else {
          const { data } = await api.get(`/whatsapp/sessions/${sessionId}/status`, {
            params: { name: deviceName },
          })
          if (data.data.sessionStatus === 'ready') {
            stopPolling()
            await ensureDeviceCreated()
          }
        }
      } catch {
        // transient errors — keep polling
      }
    }, 3000)

    return stopPolling
  }, [step, sessionId])

  const ensureDeviceCreated = async () => {
    try {
      const { data } = await api.get(`/whatsapp/sessions/${sessionId}/status`, {
        params: { name: deviceName },
      })
      if (data.data.sessionStatus === 'ready') {
        setStep('ready')
        queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY })
      }
    } catch {
      setStep('error')
      setErrorMsg('Session ready but failed to register device')
    }
  }

  if (step === 'creating') {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        <p className="text-sm text-gray-600">Starting WhatsApp session…</p>
      </div>
    )
  }

  if (step === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <XCircle className="h-10 w-10 text-red-500" />
        <p className="text-sm text-red-600">{errorMsg}</p>
        <Button variant="outline" onClick={() => { setStep('creating'); setSessionId(null); setQr(null); setErrorMsg('') }}>
          <RefreshCw className="mr-2 h-4 w-4" /> Try Again
        </Button>
      </div>
    )
  }

  if (step === 'ready') {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <CheckCircle2 className="h-12 w-12 text-green-500" />
        <p className="text-base font-semibold text-gray-900">WhatsApp connected!</p>
        <p className="text-sm text-gray-500">Your device has been added as a WhatsApp gateway.</p>
        <Button onClick={onDone}>Done</Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-5 py-4">
      <div className="mb-1 w-full">
        <label className="block text-xs font-medium text-gray-700 mb-1">Device name</label>
        <input
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
          placeholder="WhatsApp Device"
        />
      </div>

      {step === 'waiting_qr' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-green-500" />
          <p className="text-sm text-gray-500">Generating QR code…</p>
        </div>
      )}

      {(step === 'scan' || step === 'authenticating') && qr && (
        <>
          <img src={qr} alt="WhatsApp QR code" className="h-56 w-56 rounded-lg border" />
          {step === 'scan' && (
            <p className="text-sm text-gray-600 text-center max-w-xs">
              Open WhatsApp on your phone → <strong>Linked Devices</strong> → <strong>Link a Device</strong> → scan this QR
            </p>
          )}
          {step === 'authenticating' && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Authenticating…
            </div>
          )}
        </>
      )}
    </div>
  )
}
